import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const SessionTypeModal = ({
  isOpen,
  onClose,
  onSubmit,
  sessionType,
  title,
  loading,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    maxParticipants: 1,
    trainerCommissionPercentage: 30,
    description: "",
    status: "active",
    isDemo: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (sessionType) {
      setFormData({
        name: sessionType.name || "",
        maxParticipants: sessionType.maxParticipants || 1,
        trainerCommissionPercentage:
          sessionType.trainerCommissionPercentage || 30,
        description: sessionType.description || "",
        status: sessionType.status || "active",
        isDemo: sessionType.isDemo || false,
      });
    } else {
      setFormData({
        name: "",
        maxParticipants: 1,
        trainerCommissionPercentage: 30,
        description: "",
        status: "active",
        isDemo: false,
      });
    }
    setErrors({});
  }, [sessionType, isOpen]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim())
      newErrors.name = t("sessionTypeModal.errors.nameRequired");
    if (formData.maxParticipants < 1)
      newErrors.maxParticipants = t("sessionTypeModal.errors.minParticipants");
    if (
      formData.trainerCommissionPercentage < 0 ||
      formData.trainerCommissionPercentage > 100
    ) {
      newErrors.trainerCommissionPercentage = t(
        "sessionTypeModal.errors.commissionRange",
      );
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "maxParticipants" || name === "trainerCommissionPercentage"
          ? parseInt(value) || 0
          : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div
        className={`${colors.cardBg} rounded-2xl shadow-2xl border ${colors.cardBorder} w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200`}
      >
        <div
          className={`p-6 border-b ${colors.border} flex justify-between items-center bg-gray-50/50 dark:bg-white/5`}
        >
          <h2 className={`text-xl font-bold ${colors.textPrimary}`}>{title}</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-xl transition-all duration-300 ${colors.bgHover}`}
          >
            <X className={`w-5 h-5 ${colors.textSecondary}`} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5 overflow-y-auto custom-scrollbar min-h-0 flex-1">
          <div>
            <label
              className={`block text-sm font-bold uppercase tracking-wider ${colors.textSecondary} mb-2 ml-1`}
            >
              {t("sessionTypeModal.labels.name")}
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 outline-none ${
                errors.name
                  ? "border-red-500 bg-red-50/10"
                  : `${colors.inputBorder} ${colors.inputBg} focus:ring-2 ${colors.inputFocus}`
              } ${colors.inputText}`}
              placeholder={t("sessionTypeModal.placeholders.name")}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1.5 ml-1 font-medium">
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label
              className={`block text-sm font-bold uppercase tracking-wider ${colors.textSecondary} mb-2 ml-1`}
            >
              {t("sessionTypeModal.labels.maxParticipants")}
            </label>
            <input
              type="number"
              name="maxParticipants"
              min="1"
              value={formData.maxParticipants}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} outline-none focus:ring-2 ${colors.inputFocus}`}
            />
          </div>

          <div>
            <label
              className={`block text-sm font-bold uppercase tracking-wider ${colors.textSecondary} mb-2 ml-1`}
            >
              {t("sessionTypeModal.labels.status")}
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} outline-none focus:ring-2 ${colors.inputFocus}`}
            >
              <option value="active">
                {t("membershipPackageModal.options.active")}
              </option>
              <option value="inactive">
                {t("membershipPackageModal.options.inactive")}
              </option>
            </select>
          </div>

          <div>
            <label
              className={`block text-sm font-bold uppercase tracking-wider ${colors.textSecondary} mb-2 ml-1`}
            >
              {t("sessionTypeModal.labels.description")}
            </label>
            <textarea
              name="description"
              rows="3"
              value={formData.description}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} outline-none focus:ring-2 ${colors.inputFocus} resize-none`}
              placeholder={t("sessionTypeModal.placeholders.description")}
            />
          </div>

          <div className={`flex items-start gap-3 p-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg}`}>
            <input
              type="checkbox"
              id="isDemo"
              name="isDemo"
              checked={formData.isDemo}
              onChange={handleChange}
              className="mt-1 w-4 h-4 accent-rose-500 cursor-pointer"
            />
            <label htmlFor="isDemo" className="cursor-pointer">
              <span className={`block text-sm font-bold ${colors.textPrimary}`}>
                {t("sessionTypeModal.labels.isDemo")}
              </span>
              <span className={`block text-xs ${colors.textSecondary} mt-0.5`}>
                {t("sessionTypeModal.help.isDemo")}
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 py-3.5 rounded-xl font-bold transition-all duration-300 border ${colors.border} ${colors.textPrimary} ${colors.bgHover}`}
            >
              {t("sessionTypeModal.buttons.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3.5 rounded-xl font-bold transition-all duration-300 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25 disabled:opacity-50`}
            >
              {loading
                ? t("sessionTypeModal.buttons.saving")
                : sessionType
                  ? t("sessionTypeModal.buttons.update")
                  : t("sessionTypeModal.buttons.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionTypeModal;
